// Iter-194v — Manual invoice reminder dialog
//
// Opens a preview of the email that WOULD be sent (auto-picked template:
// milestone if still within delay, overdue if past), with the suggested
// recipient + cc. The CEO can edit the To/Cc/Subject/Body inline before
// sending, or just click "Send" to fire the email as-is.
//
// Iter-194z — Added a template override dropdown (auto / milestone /
// overdue) so the CEO has manual control over template picking. Changing
// the dropdown re-fetches the preview with the chosen template. If the
// CEO has already edited the subject/body, we keep their edits and only
// refresh the auto-rendered defaults when they have NOT been touched.
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Send, Loader2, AlertTriangle, Calendar, Wand2, Eye, FileEdit, ExternalLink } from "lucide-react";
import CTAInsertionHint from "@/components/CTAInsertionHint";

export default function ManualReminderDialog({ invoice, open, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  // Iter-194z — manual template override (auto = let backend decide based on date)
  const [templateChoice, setTemplateChoice] = useState("auto");
  // Iter-200g — view mode for the body: "edit" (textarea, default) or
  // "preview" (sandboxed iframe rendering the HTML with CTA banner).
  const [viewMode, setViewMode] = useState("edit");
  // Track whether the CEO manually edited subject/body so we don't blow away
  // their edits when they change the template.
  const subjectTouched = useRef(false);
  const bodyTouched = useRef(false);

  // Reset state every time the dialog opens for a different invoice.
  // Iter-200g — also reset `preview`, `toEmail`, `cc`, `subject`, `bodyText`
  // so that re-opening on a different invoice doesn't leak the previous
  // invoice's contact into the new one. The previous logic relied on
  // `if (!preview)` to seed recipients on first load, but that flag was
  // never reset between invoices → the "À" field stayed on the previous
  // client's email even after switching invoices.
  useEffect(() => {
    if (!open) return;
    setTemplateChoice("auto");
    subjectTouched.current = false;
    bodyTouched.current = false;
    setPreview(null);
    setToEmail("");
    setCc("");
    setSubject("");
    setBodyText("");
    setViewMode("edit");
  }, [open, invoice?.invoice_id]);

  // Load (or reload) the preview whenever the dialog opens OR the template
  // dropdown is changed.
  useEffect(() => {
    if (!open || !invoice?.invoice_id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/invoices/${invoice.invoice_id}/reminder-preview`, {
          params: { template: templateChoice },
        });
        if (cancelled) return;
        // Iter-200g — Seed the recipients ONLY on the first preview fetch
        // for the current invoice (i.e. when our local state is empty).
        // Subsequent template-dropdown changes must not overwrite manual
        // edits to the To/Cc fields. We key off the previously-loaded
        // preview's invoice_id rather than a stale `!preview` check, so
        // re-opening on a different invoice correctly reseeds.
        const isFirstLoadForThisInvoice = (
          !preview || preview.invoice_id !== invoice.invoice_id
        );
        setPreview({ ...r.data, invoice_id: invoice.invoice_id });
        if (isFirstLoadForThisInvoice) {
          setToEmail(r.data.suggested_to || "");
          setCc((r.data.suggested_cc || []).join(", "));
        }
        if (!subjectTouched.current) setSubject(r.data.subject || "");
        if (!bodyTouched.current) setBodyText(r.data.body || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Échec preview");
        onClose();
      } finally { setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.invoice_id, templateChoice]);

  const send = async () => {
    if (!toEmail.trim()) {
      toast.error("Adresse destinataire requise");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/invoices/${invoice.invoice_id}/send-reminder`, {
        template: templateChoice,
        to_email_override: toEmail.trim(),
        cc_emails: cc.split(",").map((s) => s.trim()).filter(Boolean),
        custom_subject: subject,
        custom_body: bodyText,
      });
      if (r.data?.ok) toast.success(`Relance envoyée à ${r.data.to}`);
      else toast.error(`Envoi échoué : ${r.data?.error || "?"} — l'entrée a été ajoutée au log.`);
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi");
    } finally { setSending(false); }
  };

  // Helper: regenerate subject/body from the latest preview (resets manual edits).
  const regenerateFromTemplate = () => {
    if (!preview) return;
    subjectTouched.current = false;
    bodyTouched.current = false;
    setSubject(preview.subject || "");
    setBodyText(preview.body || "");
    toast.success("Sujet et corps régénérés depuis le template");
  };

  const autoKind = preview?.auto_kind || (preview?.is_overdue ? "overdue" : "milestone");
  const overrideActive = templateChoice !== "auto" && templateChoice !== autoKind;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={16} className="text-zinc-700" />
            Relance facture {invoice?.number || invoice?.invoice_id}
          </DialogTitle>
          <DialogDescription>
            Aperçu de l'email — le template est auto-sélectionné selon la date d'envoi, mais vous pouvez le forcer.
          </DialogDescription>
        </DialogHeader>

        {loading && !preview && (
          <div className="py-8 text-center text-sm text-zinc-500">
            <Loader2 size={16} className="animate-spin inline mr-2" /> Préparation…
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            {/* Banner: which template was picked + why */}
            <div className={`flex items-start gap-2 border rounded-md px-3 py-2 text-xs ${
              preview.selected_kind === "overdue"
                ? "bg-red-50 border-red-200 text-red-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`} data-testid="manual-reminder-banner">
              {preview.selected_kind === "overdue" ? <AlertTriangle size={14} className="shrink-0 mt-0.5" /> : <Calendar size={14} className="shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div className="font-semibold">
                  Template : <span className="font-mono uppercase">{preview.selected_kind}</span>
                  {preview.selected_kind === "overdue" ? " (dépassement)" : ` (${preview.tier})`}
                  {overrideActive && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white text-[10px] font-mono uppercase">
                      override manuel
                    </span>
                  )}
                </div>
                <div className="opacity-90">
                  J+{preview.days_since_sent} depuis envoi · Délai contractuel {preview.delay_days}j
                  {preview.is_overdue ? ` · ${preview.days_overdue}j de retard` : ` · reste ${preview.delay_days - preview.days_since_sent}j`}
                </div>
                {overrideActive && (
                  <div className="opacity-90 mt-1">
                    Selon la date, l'envoi automatique aurait choisi <span className="font-mono uppercase">{autoKind}</span>.
                  </div>
                )}
              </div>
            </div>

            {/* Iter-194z — Template override dropdown */}
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">
                  Template à utiliser
                </Label>
                <Select
                  value={templateChoice}
                  onValueChange={(v) => setTemplateChoice(v)}
                  disabled={loading || sending}
                >
                  <SelectTrigger data-testid="manual-reminder-template-select" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto" data-testid="manual-reminder-template-auto">
                      Auto (recommandé · {autoKind})
                    </SelectItem>
                    <SelectItem value="milestone" data-testid="manual-reminder-template-milestone">
                      Milestone (relance avant échéance)
                    </SelectItem>
                    <SelectItem value="overdue" data-testid="manual-reminder-template-overdue">
                      Overdue (relance après échéance)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={regenerateFromTemplate}
                disabled={loading || sending || (!subjectTouched.current && !bodyTouched.current)}
                data-testid="manual-reminder-regenerate"
                title="Réinitialiser le sujet et le corps depuis le template"
                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Wand2 size={12} /> Régénérer
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">À</Label>
                <Input
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  data-testid="manual-reminder-to"
                  placeholder="destinataire@..."
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">Cc (séparés par virgule)</Label>
                <Input
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  data-testid="manual-reminder-cc"
                  placeholder=""
                />
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1 block">
                Sujet {subjectTouched.current && <span className="text-zinc-400 normal-case tracking-normal font-sans">· édité manuellement</span>}
              </Label>
              <Input
                value={subject}
                onChange={(e) => { subjectTouched.current = true; setSubject(e.target.value); }}
                data-testid="manual-reminder-subject"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[11px] uppercase tracking-widest font-mono text-zinc-500">
                  Corps {bodyTouched.current && <span className="text-zinc-400 normal-case tracking-normal font-sans">· édité manuellement</span>}
                </Label>
                {/* Iter-200g — toggle Édition / Aperçu visuel (iframe sandbox
                    rendant le body HTML + bannière CTA "Déclarer mon paiement"
                    exactement comme le client le verra). */}
                <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="manual-reminder-view-toggle">
                  <button
                    type="button"
                    onClick={() => setViewMode("edit")}
                    className={`inline-flex items-center gap-1 px-2.5 h-7 text-[11px] font-medium transition-colors ${viewMode === "edit" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="manual-reminder-view-edit"
                  >
                    <FileEdit size={11} /> Édition
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("preview")}
                    className={`inline-flex items-center gap-1 px-2.5 h-7 text-[11px] font-medium border-l border-zinc-200 transition-colors ${viewMode === "preview" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="manual-reminder-view-preview"
                  >
                    <Eye size={11} /> Aperçu
                  </button>
                </div>
              </div>
              {viewMode === "edit" ? (
                <>
                  <Textarea
                    value={bodyText}
                    onChange={(e) => { bodyTouched.current = true; setBodyText(e.target.value); }}
                    rows={8}
                    data-testid="manual-reminder-body"
                    className="font-mono text-xs"
                  />
                  {/* Iter-200j — Live indicator of the CTA insertion point. */}
                  {preview?.payment_declaration_url && (
                    <div className="mt-2">
                      <CTAInsertionHint text={bodyText} compact />
                    </div>
                  )}
                </>
              ) : (
                <PreviewIframe
                  bodyText={bodyText}
                  bodyHtmlFromServer={preview?.body_html}
                  paymentDeclarationUrl={preview?.payment_declaration_url}
                  edited={bodyTouched.current}
                />
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100 flex-wrap">
              {preview?.payment_declaration_url && (
                <a
                  href={preview.payment_declaration_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="manual-reminder-open-declaration"
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-xs border border-zinc-200 rounded hover:bg-zinc-50 mr-auto"
                  title="Ouvre la page de déclaration que le client verra en cliquant sur le CTA dans l'email"
                >
                  <ExternalLink size={12} /> Ouvrir page de déclaration
                </a>
              )}
              <button
                onClick={onClose}
                disabled={sending}
                className="px-3 h-9 text-xs border border-zinc-200 rounded hover:bg-zinc-50"
              >
                Annuler
              </button>
              <button
                onClick={send}
                disabled={sending || loading}
                data-testid="manual-reminder-send"
                className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-50"
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Envoyer maintenant
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



// Iter-200g — Renders the email body inside a sandboxed iframe, exactly as
// the client will see it (with the BAIM-branded CTA banner injected at the
// bottom). When the CEO has edited the textarea body we rebuild the HTML
// client-side from the latest text so the preview always reflects what
// will actually be sent.
function PreviewIframe({ bodyText, bodyHtmlFromServer, paymentDeclarationUrl, edited }) {
  const html = React.useMemo(() => {
    // Use server HTML when text wasn't edited (it already has the CTA
    // injected inline before the sign-off — see payment_cta_banner.py).
    if (!edited && bodyHtmlFromServer) {
      return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;padding:20px;background:#F4F4F5;font-family:Arial,Helvetica,sans-serif;color:#1A1F2C;font-size:14px;line-height:1.55;}</style></head><body>${bodyHtmlFromServer}</body></html>`;
    }
    // Edited mode → rebuild client-side. Convert text → HTML, then inject
    // the banner JUST BEFORE the first sign-off (Cordialement, Best regards…).
    let body = _textToHtml(bodyText || "");
    if (paymentDeclarationUrl && !body.includes(paymentDeclarationUrl)) {
      body = _injectBannerBeforeSignoff(body, _ctaBannerHtml(paymentDeclarationUrl));
    }
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;padding:20px;background:#F4F4F5;font-family:Arial,Helvetica,sans-serif;color:#1A1F2C;font-size:14px;line-height:1.55;}</style></head><body>${body}</body></html>`;
  }, [bodyText, bodyHtmlFromServer, paymentDeclarationUrl, edited]);

  return (
    <iframe
      title="Aperçu email"
      srcDoc={html}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className="w-full h-[420px] rounded border border-zinc-200 bg-zinc-50"
      data-testid="manual-reminder-preview-iframe"
    />
  );
}


function _escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function _textToHtml(text) {
  let escaped = _escapeHtml(text).replace(/\r\n|\r/g, "\n");
  // Naive URL autolink — covers http(s) only.
  escaped = escaped.replace(
    /(https?:\/\/[^\s<>]+)/g,
    (m) => `<a href="${m}" style="color:#1E3A5F;text-decoration:underline;" target="_blank" rel="noopener">${m}</a>`,
  );
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1A1F2C;line-height:1.55;white-space:normal;">${escaped.replace(/\n/g, "<br>")}</div>`;
}

function _ctaBannerHtml(url) {
  const safe = _escapeHtml(url);
  // Mirrors the inline-style banner produced by lib/payment_cta_banner.py
  // (kept in sync visually). Self-contained, no external CSS.
  return `
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:24px;margin-bottom:24px;border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;">
  <tr><td align="center" style="padding:0;">
    <table cellpadding="0" cellspacing="0" border="0" width="520" style="max-width:520px;width:100%;border-collapse:collapse;background:#FAF6EE;border:1px solid #EDE0C6;border-radius:10px;">
      <tr><td align="center" style="padding:24px 28px 20px 28px;">
        <div style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#1E3A5F;opacity:0.65;margin-bottom:8px;font-weight:600;">Paiement effectué&nbsp;?</div>
        <div style="font-size:18px;color:#1E3A5F;font-weight:700;margin-bottom:14px;line-height:1.3;">Déclarez-le en un clic sur notre plate-forme sécurisée</div>
        <div style="font-size:13px;color:#3C4858;line-height:1.55;margin-bottom:18px;max-width:420px;margin-left:auto;margin-right:auto;">Saisissez la date, le montant et la référence de votre virement&nbsp;: nous mettons à jour la facture immédiatement.</div>
        <table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;border-collapse:collapse;">
          <tr><td align="center" style="border-radius:8px;background:#1E3A5F;">
            <a href="${safe}" target="_blank" rel="noopener" style="display:inline-block;padding:12px 28px;font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:8px;font-family:Arial,Helvetica,sans-serif;letter-spacing:0.3px;">✅&nbsp;&nbsp;Déclarer le paiement de cette facture</a>
          </td></tr>
        </table>
        <div style="margin-top:14px;font-size:11px;color:#7B8794;line-height:1.4;">Lien sécurisé valable 90 jours — exclusif à cette facture.</div>
      </td></tr>
    </table>
  </td></tr>
</table>`;
}


// Iter-200i — Insert the CTA banner just BEFORE the first sign-off line in
// the HTML body (mirror of `_inject_html_before_signoff` in
// lib/payment_cta_banner.py). Falls back to appending at the end if no
// sign-off marker is detected.
function _injectBannerBeforeSignoff(html, banner) {
  if (!html) return banner;
  const re = /(cordialement|bien\s+(?:à|a)\s+vous|bien\s+cordialement|best\s+regards|sincerely|[—–-]\s*BAIM)/i;
  const m = html.match(re);
  if (!m) return html + banner;
  const idx = m.index;
  // Walk back up to 400 chars to find the closest preceding block boundary.
  const start = Math.max(0, idx - 400);
  const snippet = html.slice(start, idx);
  const markers = [snippet.lastIndexOf("<br>"), snippet.lastIndexOf("<br/>"),
                   snippet.lastIndexOf("<br />"), snippet.lastIndexOf("</p>"),
                   snippet.lastIndexOf("</div>")];
  const best = Math.max(...markers);
  let cut = idx;
  if (best !== -1) {
    cut = start + best;
    const endOfTag = html.indexOf(">", cut);
    if (endOfTag !== -1) cut = endOfTag + 1;
  }
  return html.slice(0, cut) + banner + html.slice(cut);
}
